import { Link } from 'react-router-dom';

import { DocsLayout, DocsSection } from '../../components/docs/DocsLayout';
import { CodeBlock } from '../../components/docs/CodeBlock';
import { ErrorTable } from '../../components/docs/ParamTable';
import { DOCS_NAV, PUBLIC_ERRORS } from '../../data/docs';
import { Panel } from '../../components/ui/Panel';
import { useDocumentHead } from '../../hooks/useDocumentHead';

/**
 * Public API overview, auth, billing units, limits. Endpoint detail lives on
 * per-route pages. Integrators only; no session or admin routes.
 */
export function DocsOverviewPage() {
    useDocumentHead({
        title: 'API docs · ActualSold',
        description:
            'Bearer-key eBay marketplace data API. One request is one live page of sold or active listings as JSON.',
        canonicalPath: '/docs',
    });

    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://actualsold.com';

    return (
        <DocsLayout
            title="ActualSold API"
            lead="One request returns one page of real eBay listings as JSON. Bearer key auth. Metrics and conclusions stay in your app. We ship the rows."
        >
            <DocsSection title="Base URL">
                <p>
                    Production is this origin. Every public path sits under{' '}
                    <code className="font-mono text-xs text-text">/v1</code>.
                </p>
                <CodeBlock code={origin} label="Host" />
            </DocsSection>

            <DocsSection title="Authentication">
                <p>
                    Send an API key as a Bearer token. Create keys from the dashboard after sign-in. A key
                    cannot mint another key.
                </p>
                <CodeBlock
                    label="Header"
                    code={`Authorization: Bearer as_live_…`}
                />
                <p>
                    Free signup, no card required. Keys share one account quota, so issuing a second
                    key for staging does not double entitlement.
                </p>
            </DocsSection>

            <DocsSection title="What counts as one request">
                <p>
                    One <code className="font-mono text-xs text-text">GET /v1/…</code> that reaches the
                    scraper is one billable call, whatever it returns, including few or zero rows after
                    filters. Pagination is yours: increment{' '}
                    <code className="font-mono text-xs text-text">page</code> while{' '}
                    <code className="font-mono text-xs text-text">hasNextPage</code> is true; each page
                    bills again.
                </p>
                <p>
                    Free accounts get 100 requests per calendar month. Rate limit is 60 requests per
                    minute on every plan.
                </p>
            </DocsSection>

            <DocsSection title="Errors">
                <p>
                    Failures share one JSON envelope:{' '}
                    <code className="font-mono text-xs text-text">{`{ "error": "…", "message": "…" }`}</code>
                    . A blocked eBay page is never an empty success. That would look like a dead
                    market.
                </p>
                <ErrorTable rows={PUBLIC_ERRORS} />
            </DocsSection>

            <DocsSection title="Endpoints">
                <div className="space-y-3">
                    {DOCS_NAV.filter((item) => item.to !== '/docs').map((item) => (
                        <Panel key={item.to} padding="tight">
                            <Link to={item.to} className="group block">
                                <p className="font-mono text-sm font-medium text-text group-hover:underline">
                                    GET /v1/search
                                </p>
                                <p className="mt-1 text-sm text-text-muted">{item.blurb}</p>
                            </Link>
                        </Panel>
                    ))}
                </div>
            </DocsSection>

            <DocsSection title="OpenAPI">
                <p>
                    Machine-readable schema for the endpoints on this page lives at{' '}
                    <a href="/v1/openapi.json" className="text-primary underline">
                        /v1/openapi.json
                    </a>
                    . No key needed to read it. Point your codegen or MCP client at it directly. It
                    covers <code className="font-mono text-xs text-text">/v1</code> and nothing else,
                    because <code className="font-mono text-xs text-text">/v1</code> is the whole
                    contract.
                </p>
            </DocsSection>
        </DocsLayout>
    );
}
